import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';
import { Heart, Smile, BookOpen, Brain } from 'lucide-react';

const values = [
  { icon: Heart, title: 'Gentle by design', body: 'No streaks that shame you, no guilt when you miss a day. Come back when you\'re ready.' },
  { icon: Smile, title: 'Honest with feelings', body: 'Track moods without judgment. Every emotion is valid data, not something to fix.' },
  { icon: BookOpen, title: 'Small, daily practice', body: 'Two minutes of gratitude, one mindful doodle, a single breath. Consistency over intensity.' },
  { icon: Brain, title: 'Grounded in science', body: 'Our programs draw from positive psychology, CBT, and mindfulness research — not trends.' },
];

export default function AboutPage() {
  return (
    <StaticPageLayout testId="about-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'var(--primary)', fontFamily: 'Manrope' }}>
            About
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-5 leading-tight" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            A quieter way to look after yourself.
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--muted-fg)', fontFamily: 'Georgia, serif' }}>
            Today started as a simple idea: most wellness apps are built for streaks and screenshots. We wanted to build one that feels more like a friend than a coach — something you open because you <em>want</em> to, not because a notification told you to.
          </p>
        </div>

        <section className="mb-12 rounded-2xl border p-6 sm:p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Our mission
          </h2>
          <p className="leading-relaxed" style={{ color: 'var(--text)', fontFamily: 'Georgia, serif' }}>
            To make daily mental wellness feel effortless, beautiful, and honest. We believe small, regular acts of reflection — a mood check, a gratitude note, a mindful doodle — compound into real change over time.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-bold mb-5" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            What we value
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {values.map((v, i) => (
              <div key={i} className="rounded-xl border p-5"
                style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                <v.icon className="w-5 h-5 mb-3" style={{ color: 'var(--primary)' }} />
                <h3 className="font-bold mb-1.5" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{v.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border p-6 sm:p-8" style={{ background: '#F1F4EE', borderColor: '#C9D6BC' }}>
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: '#3B4F2A' }}>
            Not a replacement for care
          </h2>
          <p className="leading-relaxed text-sm" style={{ color: '#3B4F2A' }}>
            Today is a tool to support your everyday mental wellness. It is not a substitute for professional therapy, counselling, or medical advice. If you're struggling, please reach out to a qualified provider — you deserve that kind of support too.
          </p>
        </section>
      </div>
    </StaticPageLayout>
  );
}
